import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BriefcaseIcon,
  MapPinIcon,
  ClockIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  CodeBracketIcon,
  PaintBrushIcon,
  ChartBarIcon,
  MegaphoneIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  HeartIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  AcademicCapIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

interface Job {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  experience: string;
  description: string;
  responsibilities: string[];
  requirements: string[];
}

const CareersPage: React.FC = () => {
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);

  const benefits = [
    { icon: CurrencyDollarIcon, title: 'Competitive Salary', description: 'Top-tier compensation with crypto options' },
    { icon: GlobeAltIcon, title: 'Remote First', description: 'Work from anywhere in the world' },
    { icon: HeartIcon, title: 'Health & Wellness', description: 'Comprehensive health coverage' },
    { icon: AcademicCapIcon, title: 'Learning Budget', description: 'Annual allowance for courses & conferences' },
    { icon: SparklesIcon, title: 'Token Allocation', description: 'NDH token grants for all team members' },
    { icon: RocketLaunchIcon, title: 'Career Growth', description: 'Clear paths for advancement' },
  ];

  const departments = [
    { id: 'all', name: 'All Departments', icon: BuildingOfficeIcon },
    { id: 'engineering', name: 'Engineering', icon: CodeBracketIcon },
    { id: 'design', name: 'Design', icon: PaintBrushIcon },
    { id: 'product', name: 'Product', icon: ChartBarIcon },
    { id: 'marketing', name: 'Marketing', icon: MegaphoneIcon },
    { id: 'operations', name: 'Operations', icon: ShieldCheckIcon },
  ];

  const jobs: Job[] = [
    {
      id: '1',
      title: 'Senior Smart Contract Developer',
      department: 'engineering',
      location: 'Remote',
      type: 'Full-time',
      experience: '5+ years',
      description: 'Lead the development of our core auction smart contracts on Polygon and other EVM chains.',
      responsibilities: [
        'Design and implement secure, gas-efficient smart contracts',
        'Lead smart contract audits with external security firms',
        'Mentor junior developers on Solidity best practices',
        'Collaborate with product team on new auction mechanisms',
        'Maintain and upgrade existing contract infrastructure'
      ],
      requirements: [
        '5+ years of software development experience',
        '3+ years of Solidity development',
        'Deep understanding of DeFi protocols and security patterns',
        'Experience with contract upgrades and proxy patterns',
        'Strong background in testing and formal verification'
      ]
    },
    {
      id: '2',
      title: 'Full-Stack Web3 Developer',
      department: 'engineering',
      location: 'Remote',
      type: 'Full-time',
      experience: '3+ years',
      description: 'Build and maintain our React-based frontend with Web3 integrations.',
      responsibilities: [
        'Develop responsive UI components with React and TypeScript',
        'Integrate with Ethereum wallets and smart contracts',
        'Optimize performance and user experience',
        'Implement real-time features with WebSockets',
        'Write unit and integration tests'
      ],
      requirements: [
        '3+ years of React development experience',
        'Strong TypeScript skills',
        'Experience with ethers.js or web3.js',
        'Familiarity with wallet integrations (MetaMask, WalletConnect)',
        'Understanding of blockchain fundamentals'
      ]
    },
    {
      id: '3',
      title: 'Backend Engineer',
      department: 'engineering',
      location: 'Remote / Barcelona',
      type: 'Full-time',
      experience: '4+ years',
      description: 'Build scalable backend services that power our auction platform.',
      responsibilities: [
        'Design and implement RESTful APIs and GraphQL endpoints',
        'Build event indexing services for blockchain data',
        'Optimize database queries and caching strategies',
        'Implement real-time notification systems',
        'Ensure high availability and disaster recovery'
      ],
      requirements: [
        '4+ years of backend development experience',
        'Proficiency in Node.js or Python',
        'Experience with PostgreSQL and Redis',
        'Knowledge of event-driven architectures',
        'Understanding of blockchain indexing patterns'
      ]
    },
    {
      id: '4',
      title: 'Senior Product Designer',
      department: 'design',
      location: 'Remote',
      type: 'Full-time',
      experience: '5+ years',
      description: 'Shape the user experience of our auction platform and ecosystem.',
      responsibilities: [
        'Lead end-to-end product design from research to delivery',
        'Create user flows, wireframes, and high-fidelity prototypes',
        'Conduct user research and usability testing',
        'Collaborate closely with engineering on implementation',
        'Establish and maintain design systems'
      ],
      requirements: [
        '5+ years of product design experience',
        'Strong portfolio demonstrating Web3 or fintech work',
        'Proficiency in Figma and prototyping tools',
        'Experience designing complex transaction flows',
        'Understanding of blockchain UX challenges'
      ]
    },
    {
      id: '5',
      title: 'Product Manager - Auctions',
      department: 'product',
      location: 'Remote / Barcelona',
      type: 'Full-time',
      experience: '4+ years',
      description: 'Drive the product strategy and roadmap for our auction mechanisms.',
      responsibilities: [
        'Define product requirements and prioritize features',
        'Work with engineering to deliver on roadmap',
        'Analyze user feedback and market trends',
        'Coordinate with legal on compliance requirements',
        'Present to stakeholders and community'
      ],
      requirements: [
        '4+ years of product management experience',
        'Background in DeFi, NFTs, or marketplace platforms',
        'Strong analytical and communication skills',
        'Understanding of auction theory and mechanisms',
        'Experience with agile methodologies'
      ]
    },
    {
      id: '6',
      title: 'Growth Marketing Manager',
      department: 'marketing',
      location: 'Remote',
      type: 'Full-time',
      experience: '3+ years',
      description: 'Scale our user acquisition and engagement across channels.',
      responsibilities: [
        'Develop and execute growth strategies',
        'Manage paid acquisition campaigns',
        'Optimize conversion funnels and onboarding',
        'Collaborate with community team on organic growth',
        'Report on KPIs and marketing ROI'
      ],
      requirements: [
        '3+ years of growth marketing experience',
        'Experience in crypto/Web3 marketing',
        'Proficiency with analytics and attribution tools',
        'Strong understanding of performance marketing',
        'Data-driven decision making skills'
      ]
    },
    {
      id: '7',
      title: 'Community Manager',
      department: 'marketing',
      location: 'Remote',
      type: 'Full-time',
      experience: '2+ years',
      description: 'Build and nurture our global community across Discord, Twitter, and Telegram.',
      responsibilities: [
        'Manage daily community operations',
        'Create engaging content and announcements',
        'Moderate discussions and resolve conflicts',
        'Gather and relay community feedback',
        'Organize AMAs and community events'
      ],
      requirements: [
        '2+ years of community management experience',
        'Active in Web3 communities',
        'Excellent written communication',
        'Experience with Discord bots and moderation',
        'Available for flexible hours across time zones'
      ]
    },
    {
      id: '8',
      title: 'Legal & Compliance Manager',
      department: 'operations',
      location: 'Barcelona / Remote',
      type: 'Full-time',
      experience: '5+ years',
      description: 'Navigate the regulatory landscape and ensure platform compliance.',
      responsibilities: [
        'Monitor regulatory developments globally',
        'Develop compliance policies and procedures',
        'Work with external counsel on legal matters',
        'Manage KYC/AML processes',
        'Advise product team on regulatory implications'
      ],
      requirements: [
        'Law degree with 5+ years experience',
        'Background in fintech or blockchain regulation',
        'Knowledge of EU and international regulations',
        'Experience with AML/KYC frameworks',
        'Strong analytical and communication skills'
      ]
    }
  ];

  const filteredJobs = selectedDepartment === 'all'
    ? jobs
    : jobs.filter(job => job.department === selectedDepartment);

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <BriefcaseIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Join Our <span className="text-primary-400">Team</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Help us build the future of decentralized commerce. We're looking for passionate individuals to join our mission.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <span className="px-4 py-2 bg-dark-800 rounded-full text-gray-300">
                <UserGroupIcon className="w-4 h-4 inline mr-2" />
                50+ Team Members
              </span>
              <span className="px-4 py-2 bg-dark-800 rounded-full text-gray-300">
                <GlobeAltIcon className="w-4 h-4 inline mr-2" />
                15+ Countries
              </span>
              <span className="px-4 py-2 bg-dark-800 rounded-full text-gray-300">
                <BuildingOfficeIcon className="w-4 h-4 inline mr-2" />
                Remote First
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-dark-900/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Why Work With Us?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, idx) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-dark-800/50 rounded-xl p-6 border border-dark-700"
                  >
                    <Icon className="w-8 h-8 text-primary-400 mb-3" />
                    <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                    <p className="text-gray-400 text-sm">{benefit.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">Open Positions</h2>
              <p className="text-gray-400">Find your next opportunity at TEMPLUM</p>
            </div>

            {/* Department Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {departments.map((dept) => {
                const Icon = dept.icon;
                return (
                  <button
                    key={dept.id}
                    onClick={() => setSelectedDepartment(dept.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      selectedDepartment === dept.id
                        ? 'bg-primary-500 text-white'
                        : 'bg-dark-800 text-gray-400 hover:bg-dark-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {dept.name}
                  </button>
                );
              })}
            </div>

            {/* Job Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              {filteredJobs.map((job) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 hover:border-primary-500/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedJob(job)}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-1">{job.title}</h3>
                      <p className="text-primary-400 text-sm capitalize">{job.department}</p>
                    </div>
                    <span className="px-3 py-1 bg-primary-500/20 text-primary-400 text-sm rounded-full">
                      {job.type}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{job.description}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <MapPinIcon className="w-4 h-4" />
                      {job.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <ClockIcon className="w-4 h-4" />
                      {job.experience}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            {filteredJobs.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400">No open positions in this department. Check back soon!</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Job Detail Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-dark-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-dark-700">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">{selectedJob.title}</h2>
                  <p className="text-primary-400 capitalize">{selectedJob.department}</p>
                </div>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  &times;
                </button>
              </div>
              <div className="flex flex-wrap gap-4 mt-4 text-sm">
                <span className="flex items-center gap-1 text-gray-400">
                  <MapPinIcon className="w-4 h-4" />
                  {selectedJob.location}
                </span>
                <span className="flex items-center gap-1 text-gray-400">
                  <ClockIcon className="w-4 h-4" />
                  {selectedJob.experience}
                </span>
                <span className="px-3 py-1 bg-primary-500/20 text-primary-400 rounded-full">
                  {selectedJob.type}
                </span>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">About the Role</h3>
                <p className="text-gray-300">{selectedJob.description}</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Responsibilities</h3>
                <ul className="space-y-2">
                  {selectedJob.responsibilities.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-300">
                      <span className="w-1.5 h-1.5 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Requirements</h3>
                <ul className="space-y-2">
                  {selectedJob.requirements.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-300">
                      <span className="w-1.5 h-1.5 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pt-4 border-t border-dark-700">
                <a
                  href={`mailto:careers@nardiha.com?subject=Application: ${selectedJob.title}`}
                  className="block w-full text-center px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
                >
                  Apply for this Position
                </a>
                <p className="text-gray-500 text-sm text-center mt-3">
                  Send your resume and portfolio to careers@nardiha.com
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-500/20 to-purple-500/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Don't See a Perfect Fit?
            </h2>
            <p className="text-gray-300 mb-8">
              We're always looking for talented people. Send us your resume and tell us how you can contribute to our mission.
            </p>
            <a
              href="mailto:careers@nardiha.com"
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
            >
              Send Open Application
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
